import {
  BattlePlayerInfo,
  ChallengeReceivedPayload,
  ChallengeSentPayload,
  ColorRank,
  RadarSearchMode,
} from "../types/battle"
import { normalizeGuardianPath } from "../constants/guardianPaths"
import { normalizeColorRank, normalizeRadarSearchMode } from "./radarStatus"
import { normalizeCardElement } from "./cardElement"

function pickNumber(value: unknown, fallback = 0): number {
  if (typeof value === "number" && Number.isFinite(value)) return value
  if (typeof value === "string" && value.trim()) {
    const n = Number(value)
    return Number.isFinite(n) ? n : fallback
  }
  return fallback
}

function pickString(value: unknown, fallback = ""): string {
  if (typeof value === "string") return value
  if (value != null) return String(value)
  return fallback
}

function pickLeague(value: unknown): BattlePlayerInfo["academicLeague"] {
  const raw = pickString(value).toUpperCase()
  if (raw === "TITAN" || raw === "ATLANT") return raw
  return null
}

/** WS challenge-* / battle-started — rating, avatar и snake_case */
export function normalizeBattlePlayerInfo(raw: unknown): BattlePlayerInfo | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const userId = pickNumber(obj.userId ?? obj.user_id, NaN)
  if (!Number.isFinite(userId)) return null

  const globalPts = pickNumber(
    obj.globalPts ?? obj.global_pts ?? obj.rating ?? obj.ratingPts ?? obj.rating_pts
  )

  return {
    userId,
    nickname: pickString(obj.nickname, "Хранитель"),
    publicGameCode: pickString(obj.publicGameCode ?? obj.public_game_code),
    globalPts,
    localPts: pickNumber(obj.localPts ?? obj.local_pts),
    academicLeague: pickLeague(obj.academicLeague ?? obj.academic_league),
    guardianPath: normalizeGuardianPath(obj.guardianPath ?? obj.guardian_path),
    affiliationElement: normalizeCardElement(
      obj.affiliationElement ?? obj.affiliation_element ?? obj.element
    ),
    avatarVariant: (() => {
      const raw = obj.avatarVariant ?? obj.avatar_variant
      if (raw == null || raw === "") return 1
      const n = Math.floor(pickNumber(raw, 1))
      return n >= 1 && n <= 3 ? n : 1
    })(),
    profileFrameKey:
      pickString(obj.profileFrameKey ?? obj.profile_frame_key) || null,
    cityName: pickString(obj.cityName ?? obj.city_name) || null,
    guildTag: (() => {
      const g = obj.guild
      if (g && typeof g === "object") {
        return pickString((g as Record<string, unknown>).tag) || null
      }
      return pickString(obj.guildTag ?? obj.guild_tag) || null
    })(),
    guildName: (() => {
      const g = obj.guild
      if (g && typeof g === "object") {
        return pickString((g as Record<string, unknown>).name) || null
      }
      return pickString(obj.guildName ?? obj.guild_name) || null
    })(),
    guildEmblemKey: (() => {
      const g = obj.guild
      if (g && typeof g === "object") {
        return (
          pickString(
            (g as Record<string, unknown>).emblemKey ??
              (g as Record<string, unknown>).emblem_key
          ) || null
        )
      }
      return (
        pickString(obj.guildEmblemKey ?? obj.guild_emblem_key) || null
      )
    })(),
  }
}

export function normalizeChallengeReceivedPayload(
  raw: unknown
): ChallengeReceivedPayload | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const challengeId = pickString(obj.challengeId ?? obj.challenge_id)
  if (!challengeId) return null

  const challenger = normalizeBattlePlayerInfo(obj.challenger)
  if (!challenger) return null

  const searchMode =
    normalizeRadarSearchMode(obj.searchMode ?? obj.search_mode) ?? "RATING"
  const colorRank = normalizeColorRank(obj.colorRank ?? obj.color_rank)

  return {
    challengeId,
    expiresInSec: pickNumber(obj.expiresInSec ?? obj.expires_in_sec, 60),
    searchMode,
    colorRank: searchMode === "RANK" ? colorRank : undefined,
    challenger,
  }
}

export function normalizeChallengeSentPayload(raw: unknown): ChallengeSentPayload | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const challengeId = pickString(obj.challengeId ?? obj.challenge_id)
  if (!challengeId) return null

  const target = normalizeBattlePlayerInfo(obj.target)
  if (!target) return null

  const searchMode =
    normalizeRadarSearchMode(obj.searchMode ?? obj.search_mode) ?? "RATING"
  const colorRank = normalizeColorRank(obj.colorRank ?? obj.color_rank)

  return {
    challengeId,
    expiresInSec: pickNumber(obj.expiresInSec ?? obj.expires_in_sec, 60),
    target,
    searchMode,
    colorRank: searchMode === "RANK" ? colorRank : undefined,
  }
}

export function normalizeChallengeStatusPayload(
  raw: unknown
): { challengeId: string; message: string } | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const challengeId = pickString(obj.challengeId ?? obj.challenge_id)
  if (!challengeId) return null
  return {
    challengeId,
    message: pickString(obj.message, "Ошибка вызова"),
  }
}
